import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import supabase from '../supabase';

const AppContext = createContext();

export const JOB_CATEGORIES = [
  'Full-Time',
  'Part-Time',
  'Seasonal',
  'Internship',
  'Contract',
  'Volunteer'
];

export const JOB_TYPES = [
  'Bicycle',
  'Camp',
  'Climb/Mountaineer',
  'Environmental',
  'Medical/Rescue',
  'Other Outdoor',
  'Snow/Winter',
  'Trek/Backpack',
  'Water'
];

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fallback check for the site admin email to ensure access even if profile role is missing
  const isAdmin = profile?.role === 'admin' || user?.email === 'dwayne@adventurejobs.co';

  useEffect(() => {
    fetchJobs();
    fetchProducts();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        setUser(session.user);
        fetchProfile(session.user.id);
      } else {
        setUser(null);
        setProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('jobs_20240320')
        .select(`
          *,
          profile:employer_id (org_name, logo_url, email),
          locations:job_locations_20240320 (*),
          tags:job_tags_20240320 (*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (err) {
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products_20240320')
        .select('*')
        .eq('is_active', true);
      if (error) throw error;
      setProducts(data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles_20240320')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      setProfile(data);
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  const geocodeLocation = async (locationName) => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationName)}&limit=1`);
      const data = await response.json();
      if (data && data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        };
      }
    } catch (err) {
      console.error('Geocoding error:', err);
    }
    return { lat: null, lng: null };
  };

  const triggerSitemapPing = async () => {
    try {
      await supabase.functions.invoke('generate_sitemap', {
        queryParams: { ping: 'true' }
      });
    } catch (err) {
      console.error('Sitemap trigger error:', err);
    }
  };

  const triggerAutomatedEmail = async (slug, toEmail, metadata = {}) => {
    if (!toEmail || !toEmail.includes('@')) {
      console.error(`Invalid recipient email for ${slug}:`, toEmail);
      return { success: false, error: 'Invalid email' };
    }

    try {
      const { data: template, error: templateError } = await supabase
        .from('email_templates_20240320')
        .select('*')
        .eq('slug', slug)
        .single();

      if (templateError) throw templateError;

      let subject = template.subject;
      let bodyHtml = template.body_html;

      Object.entries(metadata).forEach(([key, value]) => {
        const placeholder = new RegExp(`{{${key}}}`, 'g');
        subject = subject.replace(placeholder, value || '');
        bodyHtml = bodyHtml.replace(placeholder, value || '');
      });

      const { data: queueItem, error: queueError } = await supabase
        .from('email_queue_20240320')
        .insert([{
          to_email: toEmail,
          subject,
          body_html: bodyHtml,
          metadata,
          status: 'pending'
        }])
        .select()
        .single();

      if (queueError) throw queueError;

      await supabase.functions.invoke('socketlabs_send_email', {
        body: { queueId: queueItem.id }
      });

      return { success: true };
    } catch (err) {
      console.error(`Email trigger error (${slug}):`, err);
      return { success: false, error: err.message };
    }
  };

  const addJob = async (jobData, locations, tags, honeypot = '') => {
    if (honeypot && honeypot.trim() !== '') {
      console.warn('Spam submission detected via honeypot (AddJob)');
      return { success: true }; 
    }

    try {
      const isPaidListing = !!(jobData.product_id && jobData.product_id.trim() !== '');
      const targetStatus = isPaidListing ? 'draft' : 'pending';
      
      const sanitizedData = {
        ...jobData,
        employer_id: user.id,
        product_id: isPaidListing ? jobData.product_id : null,
        coupon_code: jobData.coupon_code || null,
        status: targetStatus 
      };

      const { data: job, error: jobError } = await supabase
        .from('jobs_20240320')
        .insert([sanitizedData])
        .select()
        .single();

      if (jobError) throw jobError;

      if (locations && locations.length > 0) {
        const locationsWithGeo = await Promise.all(locations.map(async (loc) => {
          const coords = await geocodeLocation(loc.name);
          return {
            job_id: job.id,
            name: loc.name,
            ...coords
          };
        }));
        await supabase.from('job_locations_20240320').insert(locationsWithGeo);
      }

      if (tags && tags.length > 0) {
        const tagObjects = tags.map(t => ({ job_id: job.id, tag: t }));
        await supabase.from('job_tags_20240320').insert(tagObjects);
      }

      if (!isPaidListing) {
        await triggerAutomatedEmail('job_received', user.email, {
          job_title: job.title,
          org_name: profile?.org_name || 'Employer'
        });
      }

      await fetchJobs();
      return { success: true, job };
    } catch (err) {
      console.error('Error adding job:', err);
      return { success: false, error: err.message };
    }
  };

  const updateJob = async (jobId, jobData, locations, tags) => {
    try {
      const { error: jobError } = await supabase
        .from('jobs_20240320')
        .update(jobData)
        .eq('id', jobId);

      if (jobError) throw jobError;

      await supabase.from('job_locations_20240320').delete().eq('job_id', jobId);
      if (locations && locations.length > 0) {
        const locationsWithGeo = await Promise.all(locations.map(async (loc) => {
          const coords = await geocodeLocation(loc.name);
          return {
            job_id: jobId,
            name: loc.name,
            ...coords
          };
        }));
        await supabase.from('job_locations_20240320').insert(locationsWithGeo);
      }

      await supabase.from('job_tags_20240320').delete().eq('job_id', jobId);
      if (tags && tags.length > 0) {
        const tagObjects = tags.map(t => ({ job_id: jobId, tag: t }));
        await supabase.from('job_tags_20240320').insert(tagObjects);
      }

      await fetchJobs();
      return { success: true };
    } catch (err) {
      console.error('Error updating job:', err);
      return { success: false, error: err.message };
    }
  };

  const updateJobStatus = async (jobId, status) => {
    try {
      const { error } = await supabase
        .from('jobs_20240320')
        .update({ status })
        .eq('id', jobId);

      if (error) throw error;
      
      if (status === 'active' || status === 'published') {
        triggerSitemapPing();
        
        const { data: job, error: fetchErr } = await supabase
          .from('jobs_20240320')
          .select('*, profile:employer_id(email, org_name)')
          .eq('id', jobId)
          .single();

        if (job && !fetchErr) {
          const recipientEmail = job.profile?.email;
          if (recipientEmail) {
            await triggerAutomatedEmail('job_published', recipientEmail, {
              job_title: job.title,
              org_name: job.profile?.org_name || 'Employer'
            });
          }
        }
      }
      
      await fetchJobs();
      return { success: true };
    } catch (err) {
      console.error('Error updating job status:', err);
      return { success: false, error: err.message };
    }
  };

  const renewJob = async (jobId) => {
    try {
      // Find the current job to see its existing expiry
      const currentJob = jobs.find(j => j.id === jobId);
      const now = new Date();
      
      // Calculate base date: if the job is already expired, start from now.
      // If it's still active but we're extending it, start from the current expiry date.
      let baseDate = now;
      if (currentJob?.expires_at) {
        const currentExpiry = new Date(currentJob.expires_at);
        if (currentExpiry > now) {
          baseDate = currentExpiry;
        }
      }

      const newExpiry = new Date(baseDate.getTime() + (30 * 24 * 60 * 60 * 1000));

      const { error } = await supabase
        .from('jobs_20240320')
        .update({ 
          status: 'active',
          expires_at: newExpiry.toISOString(),
          last_renewed_at: now.toISOString(),
          expiry_warning_sent_at: null,
          expiry_email_sent_at: null
        })
        .eq('id', jobId);

      if (error) throw error;
      
      console.log(`Job ${jobId} renewed. New expiry: ${newExpiry.toISOString()}`);
      await fetchJobs();
      return { success: true };
    } catch (err) {
      console.error('Error renewing job:', err);
      return { success: false, error: err.message };
    }
  };

  const deleteJob = async (jobId) => {
    try {
      const { error } = await supabase.from('jobs_20240320').delete().eq('id', jobId);
      if (error) throw error;
      await fetchJobs();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const relistJob = async (jobId) => {
    try {
      const { error } = await supabase
        .from('jobs_20240320')
        .update({ 
          status: 'pending', 
          expires_at: null,
          expiry_warning_sent_at: null,
          expiry_email_sent_at: null
        })
        .eq('id', jobId);

      if (error) throw error;
      await fetchJobs();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const createCheckoutSession = async (jobId, productId, couponCode, successUrl, cancelUrl) => {
    try {
      const { data, error } = await supabase.functions.invoke('stripe_checkout', {
        body: { jobId, productId, couponCode, successUrl, cancelUrl }
      });
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Checkout error:', err);
      throw err;
    }
  };

  const uploadLogo = async (file) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id || 'temp'}-${Math.random()}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(filePath);

      return { success: true, url: publicUrl };
    } catch (err) {
      console.error('Error uploading logo:', err);
      return { success: false, error: err.message };
    }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { success: false, error: error.message };
    return { success: true, user: data.user };
  };

  const signUp = async (email, password, orgName, logoUrl, honeypot = '') => {
    if (honeypot && honeypot.trim() !== '') {
      console.warn('Spam submission detected via honeypot (SignUp)');
      return { success: true };
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { org_name: orgName, logo_url: logoUrl }
      }
    });
    if (error) return { success: false, error: error.message };
    return { success: true, user: data.user, needsConfirmation: data.session === null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const updateProfile = async (updates) => {
    try {
      const { error } = await supabase
        .from('profiles_20240320')
        .update(updates)
        .eq('id', user.id);
      if (error) throw error;
      await fetchProfile(user.id);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const value = {
    user,
    profile,
    isAdmin,
    jobs,
    products,
    loading,
    addJob,
    updateJob,
    updateJobStatus,
    renewJob,
    deleteJob,
    relistJob,
    uploadLogo,
    fetchJobs,
    fetchProfile,
    signIn,
    signUp,
    signOut,
    updateProfile,
    createCheckoutSession
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => useContext(AppContext);